import { collabDatabaseBatch } from '@databases/collab-state-manager/batch';
import { getCachedViewSnapshot } from '@databases/database-state/state/view';
import { getService } from '@databases/service-container';
import {
  DatabaseFieldType,
  type DatabaseEntry,
  type DatabaseValue
} from '@databases/types/database';
import {
  DatabaseLayoutType,
  type DatabaseViewSettings
} from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  applyPageLabels,
  getContentLabels
} from '@databases/utils/database/field-types/pageLabels';
import {
  applyPageStatus,
  getReferencedStatusContentRefs
} from '@databases/utils/database/field-types/pageStatus';
import {
  createEntry,
  createField,
  getDatabaseEntryOptional,
  getExistingFieldNames
} from '@databases/utils/database/utils';
import {
  applyDatabaseView,
  getOrderWithFallback,
  getHiddenFields,
  getViewSpecificOrderingExperimentValue
} from '@databases/utils/database/view/view';
import { getUniqueFieldName } from '@databases/utils/field-name';
import { afterNextLayout } from '@databases/utils/lib/raf';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch } from '@databases/utils/redux/redux';
import { generateId } from '@databases/utils/utils';
import { cloneDeep } from 'lodash-es';
import { type AnyAction } from 'redux';
import { type ThunkAction, type ThunkDispatch } from 'redux-thunk';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import { addChangeToDraft } from '../changesets/actions.ts';
import { type DefaultRootState } from '../default-root-state.ts';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

const reselectEntriesAfterNextLayout = (entryIds: readonly string[]): void => {
  if (entryIds.length === 0) {
    return;
  }

  afterNextLayout(() => {
    batch(() => {
      const selectionsActions = getService('SelectionsActions');
      selectionsActions.deselectAll();
      selectionsActions.selectEntries([...entryIds]);
    });
  });
};

const reselectFieldsAfterNextLayout = (fieldIds: readonly string[]): void => {
  if (fieldIds.length === 0) {
    return;
  }

  afterNextLayout(() => {
    batch(() => {
      const selectionsActions = getService('SelectionsActions');
      selectionsActions.deselectAll();
      selectionsActions.selectFields([...fieldIds]);
    });
  });
};

const getBoardGroupingFieldIds = (viewSettings: DatabaseViewSettings): string[] => {
  const fieldIds = [
    viewSettings.grouping?.groupBy?.fieldId ?? viewSettings.layout.groupFieldId,
    viewSettings.grouping?.subgroupBy?.fieldId
  ];

  return [...new Set(fieldIds.filter((fieldId) => fieldId !== undefined))];
};

const getFirstGroupingValue = (value: DatabaseValue): DatabaseValue => {
  const clonedValue = cloneDeep(value);

  if (Array.isArray(clonedValue.value)) {
    clonedValue.value = clonedValue.value.slice(0, 1) as typeof clonedValue.value;
  }

  return clonedValue;
};

const applyBoardGroupingValuesToEntry = (
  entry: DatabaseEntry,
  sourceEntry: DatabaseEntry | undefined,
  viewSettings: DatabaseViewSettings | undefined
): DatabaseEntry => {
  if (viewSettings?.layout?.type !== DatabaseLayoutType.BOARD || !sourceEntry) {
    return entry;
  }

  for (const fieldId of getBoardGroupingFieldIds(viewSettings)) {
    const value = sourceEntry.values[fieldId];
    if (value) {
      entry.values[fieldId] = getFirstGroupingValue(value);
    }
  }

  return entry;
};

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity between implicit and explicit selection inputs.
 */
const insertEntriesInternal = (
  contentId: string,
  viewSettings: DatabaseViewSettings | undefined,
  insertBefore: boolean,
  selectedEntryIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const database = selectDatabaseByContentIdRequired(getState(), {
    contentId
  });

  if (selectedEntryIds.length === 0) {
    return;
  }

  const useViewOrder = getViewSpecificOrderingExperimentValue();
  const currentView = useViewOrder ? getCachedViewSnapshot() : undefined;
  const selectedEntryIdSet = new Set(selectedEntryIds);
  const orderedSelectedEntryIds = currentView
    ? getOrderWithFallback(database.entries, currentView.settings.entryOrder).filter(
        (entryId) => selectedEntryIdSet.has(entryId)
      )
    : selectedEntryIds;
  const aboveOrBelowSelectedEntry = insertBefore
    ? orderedSelectedEntryIds[0]
    : orderedSelectedEntryIds[orderedSelectedEntryIds.length - 1];

  if (aboveOrBelowSelectedEntry === undefined) {
    return;
  }

  const sourceEntry = getDatabaseEntryOptional(database, aboveOrBelowSelectedEntry);
  const entry = applyBoardGroupingValuesToEntry(
    createEntry(database, viewSettings),
    sourceEntry,
    viewSettings
  );
  const index = database.entries.indexOf(aboveOrBelowSelectedEntry);
  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.ADD_ENTRY,
      entry,
      index: insertBefore ? index : index + 1,
      ...(currentView && {
        viewPlacement: {
          type: 'relative' as const,
          currentViewId: currentView.id,
          anchorEntryId: aboveOrBelowSelectedEntry,
          position: insertBefore ? ('before' as const) : ('after' as const)
        }
      })
    })
  );
  reselectEntriesAfterNextLayout([entry.id]);
};

/**
 * @deprecated Use `doInsertEntriesWithIds` instead to avoid implicit selection lookup.
 */
export const doInsertEntries =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined,
    insertBefore: boolean
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    insertEntriesInternal(
      contentId,
      viewSettings,
      insertBefore,
      reader.getEntryIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doInsertEntriesWithIds =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined,
    insertBefore: boolean,
    selectedEntryIds: string[]
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    insertEntriesInternal(
      contentId,
      viewSettings,
      insertBefore,
      selectedEntryIds,
      dispatch,
      getState
    );
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity between implicit and explicit selection inputs.
 */
const duplicateEntriesInternal = (
  contentId: string,
  selectedEntryIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>
) => {
  if (selectedEntryIds.length === 0) {
    return;
  }

  const clonedEntryIds = Object.fromEntries(
    selectedEntryIds.map((entryId) => [entryId, generateId()])
  );
  const currentView = getViewSpecificOrderingExperimentValue()
    ? getCachedViewSnapshot()
    : undefined;

  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.CLONE_ENTRIES,
      entryIds: clonedEntryIds,
      ...(currentView && {
        viewPlacement: {
          type: 'after-source-block' as const,
          currentViewId: currentView.id
        }
      })
    })
  );
  reselectEntriesAfterNextLayout(Object.values(clonedEntryIds));
};

/**
 * @deprecated Use `doDuplicateEntireEntriesWithIds` instead to avoid implicit selection lookup.
 */
export const doDuplicateEntireEntries =
  (contentId: string): Dispatcher<void> =>
  (dispatch) => {
    const reader = getService('SelectionsReader');
    duplicateEntriesInternal(contentId, reader.getSelectedEntryIds(), dispatch);
  };

export const doDuplicateEntireEntriesWithIds =
  (contentId: string, selectedEntryIds: string[]): Dispatcher<void> =>
  (dispatch) => {
    duplicateEntriesInternal(contentId, selectedEntryIds, dispatch);
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity between implicit and explicit selection inputs.
 */
const clearEntriesInternal = (
  contentId: string,
  selectedEntryIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>
) => {
  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.CLEAR_ENTRIES,
      entryIds: selectedEntryIds
    })
  );
};

/**
 * @deprecated Use `doClearEntireEntriesWithIds` instead to avoid implicit selection lookup.
 */
export const doClearEntireEntries =
  (contentId: string): Dispatcher<void> =>
  (dispatch) => {
    const reader = getService('SelectionsReader');
    clearEntriesInternal(contentId, reader.getSelectedEntryIds(), dispatch);
  };

export const doClearEntireEntriesWithIds =
  (contentId: string, selectedEntryIds: string[]): Dispatcher<void> =>
  (dispatch) => {
    clearEntriesInternal(contentId, selectedEntryIds, dispatch);
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity between implicit and explicit selection inputs.
 */
const deleteEntriesInternal = (
  contentId: string,
  selectedEntryIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  if (selectedEntryIds.length === 0) {
    return;
  }

  batch(() => {
    collabDatabaseBatch({ dispatch, getState }, contentId, () => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.DELETE_ENTRY,
          entryIds: selectedEntryIds
        })
      );
    });
  });
};

/**
 * @deprecated Use `doDeleteEntriesWithIds` instead to avoid implicit selection lookup.
 */
export const doDeleteEntries =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    deleteEntriesInternal(
      contentId,
      reader.getEntryIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doDeleteEntriesWithIds =
  (contentId: string, selectedEntryIds: string[]): Dispatcher<void> =>
  (dispatch, getState) => {
    deleteEntriesInternal(contentId, selectedEntryIds, dispatch, getState);
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity between implicit and explicit selection inputs.
 */
const moveEntriesInternal = (
  contentId: string,
  moveUp: boolean,
  selectedEntryIds: readonly string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const database = selectDatabaseByContentIdRequired(getState(), {
    contentId
  });
  const view = getCachedViewSnapshot();
  const viewSpecificOrderingEnabled = getViewSpecificOrderingExperimentValue();
  const databaseDraft = viewSpecificOrderingEnabled
    ? applyDatabaseView(database, database, view.settings, getState())
    : database;
  const firstOrLastEntryId = moveUp
    ? selectedEntryIds[0]
    : selectedEntryIds[selectedEntryIds.length - 1];
  const currentIndexInEntryIds = databaseDraft.entries.indexOf(firstOrLastEntryId);
  const requestedViewIndex = moveUp
    ? currentIndexInEntryIds - 1
    : currentIndexInEntryIds + 2;
  const viewAnchor = databaseDraft.entries[requestedViewIndex];
  const newIndex =
    viewSpecificOrderingEnabled && view.settings.layout.type === DatabaseLayoutType.BOARD
      ? viewAnchor
        ? database.entries.indexOf(viewAnchor)
        : database.entries.length
      : requestedViewIndex;
  batch(() => {
    collabDatabaseBatch({ dispatch, getState }, contentId, () => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.REORDER_ENTRIES,
          entryIds: [...selectedEntryIds],
          newIndex
        })
      );
    });
  });
  reselectEntriesAfterNextLayout(selectedEntryIds);
};

/**
 * @deprecated Use `doMoveEntriesWithIds` instead to avoid implicit selection lookup.
 */
export const doMoveEntries =
  (contentId: string, moveUp: boolean): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    moveEntriesInternal(
      contentId,
      moveUp,
      reader.getEntryIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doMoveEntriesWithIds =
  (
    contentId: string,
    moveUp: boolean,
    selectedEntryIds: readonly string[]
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    moveEntriesInternal(contentId, moveUp, selectedEntryIds, dispatch, getState);
  };

const getFieldInsertionAnchor = (
  fieldIds: readonly string[],
  currentViewSettings: DatabaseViewSettings | undefined,
  selectedFieldIds: readonly string[],
  insertBefore: boolean
): string | undefined => {
  const selectedFieldIdSet = new Set(selectedFieldIds);
  const orderedSelectedFieldIds = currentViewSettings
    ? getOrderWithFallback(fieldIds, currentViewSettings.fieldOrder).filter((fieldId) =>
        selectedFieldIdSet.has(fieldId)
      )
    : selectedFieldIds;

  return insertBefore
    ? orderedSelectedFieldIds[0]
    : orderedSelectedFieldIds[orderedSelectedFieldIds.length - 1];
};

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity between implicit and explicit selection inputs.
 */
const insertFieldsInternal = (
  contentId: string,
  currentViewId: string,
  insertBefore: boolean,
  fieldName: string,
  selectedFieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const database = selectDatabaseByContentIdRequired(getState(), {
    contentId
  });
  const newField = createField(DatabaseFieldType.TEXT);
  const uniqueFieldName = getUniqueFieldName(
    fieldName,
    getExistingFieldNames(database, newField)
  );

  const useViewOrder = getViewSpecificOrderingExperimentValue();
  const currentView = useViewOrder ? getCachedViewSnapshot() : undefined;
  const beforeOrAfterSelectedField = getFieldInsertionAnchor(
    database.fields,
    currentView?.settings,
    selectedFieldIds,
    insertBefore
  );

  if (beforeOrAfterSelectedField === undefined) {
    return;
  }

  const index = database.fields.indexOf(beforeOrAfterSelectedField);
  if (index === -1) {
    return;
  }

  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.ADD_FIELD,
      field: { ...newField, name: uniqueFieldName },
      index: insertBefore ? index : index + 1,
      currentViewId,
      ...(currentView && {
        viewPlacement: {
          type: 'relative' as const,
          currentViewId: currentView.id,
          anchorFieldId: beforeOrAfterSelectedField,
          position: insertBefore ? ('before' as const) : ('after' as const)
        }
      })
    })
  );
  reselectFieldsAfterNextLayout([newField.id]);
};

/**
 * @deprecated Use `doInsertFieldsWithIds` instead to avoid implicit selection lookup.
 */
export const doInsertFields =
  (
    contentId: string,
    currentViewId: string,
    insertBefore: boolean,
    fieldName: string
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    insertFieldsInternal(
      contentId,
      currentViewId,
      insertBefore,
      fieldName,
      reader.getFieldIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doInsertFieldsWithIds =
  (
    contentId: string,
    currentViewId: string,
    insertBefore: boolean,
    fieldName: string,
    selectedFieldIds: string[]
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    insertFieldsInternal(
      contentId,
      currentViewId,
      insertBefore,
      fieldName,
      selectedFieldIds,
      dispatch,
      getState
    );
  };

/**
 * Internal helper for duplicating entire fields.
 */
const duplicateEntireFieldsInternal = (
  contentId: string,
  currentViewId: string,
  fieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>
) => {
  if (fieldIds.length === 0) {
    return;
  }

  const clonedFieldIds = Object.fromEntries(
    fieldIds.map((fieldId) => [fieldId, generateId()])
  );

  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.CLONE_FIELDS,
      fieldIds: clonedFieldIds,
      currentViewId,
      ...(getViewSpecificOrderingExperimentValue() && {
        viewPlacement: {
          type: 'after-source-block' as const,
          currentViewId
        }
      })
    })
  );
  reselectFieldsAfterNextLayout(Object.values(clonedFieldIds));
};

/**
 * @deprecated Use `doDuplicateEntireFieldsWithIds` instead to avoid implicit selection lookup.
 */
export const doDuplicateEntireFields =
  (contentId: string, currentViewId: string): Dispatcher<void> =>
  (dispatch) => {
    const reader = getService('SelectionsReader');
    duplicateEntireFieldsInternal(
      contentId,
      currentViewId,
      reader.getSelectedFieldIds(),
      dispatch
    );
  };

export const doDuplicateEntireFieldsWithIds =
  (contentId: string, currentViewId: string, fieldIds: string[]): Dispatcher<void> =>
  (dispatch) => {
    duplicateEntireFieldsInternal(contentId, currentViewId, fieldIds, dispatch);
  };

/**
 * Internal helper for clearing entire fields.
 */
const clearEntireFieldsInternal = (
  contentId: string,
  fieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>
) => {
  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.CLEAR_ENTRIES,
      fieldIds
    })
  );
};

/**
 * @deprecated Use `doClearEntireFieldsWithIds` instead to avoid implicit selection lookup.
 */
export const doClearEntireFields =
  (contentId: string): Dispatcher<void> =>
  (dispatch) => {
    const reader = getService('SelectionsReader');
    clearEntireFieldsInternal(contentId, reader.getSelectedFieldIds(), dispatch);
  };

export const doClearEntireFieldsWithIds =
  (contentId: string, fieldIds: string[]): Dispatcher<void> =>
  (dispatch) => {
    clearEntireFieldsInternal(contentId, fieldIds, dispatch);
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity between implicit and explicit selection inputs.
 */
const deleteFieldsInternal = (
  contentId: string,
  fieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState,
  onFieldsDeleted?: () => void
) => {
  if (fieldIds.length === 0) {
    return;
  }

  const reliability = ReliabilityMetrics.getInstance();

  try {
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.DELETE_FIELD,
            fieldIds
          })
        );
        onFieldsDeleted?.();
      });
    });
    reliability.success(MetricName.DATABASE_DELETE_FIELD);
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : String(e);
    reliability.failure(MetricName.DATABASE_DELETE_FIELD, {
      errorMessage
    });
    throw e;
  }
};

/**
 * @deprecated Use `doDeleteFieldsWithIds` instead to avoid implicit selection lookup.
 */
export const doDeleteFields =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    deleteFieldsInternal(
      contentId,
      reader.getFieldIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doDeleteFieldsWithIds =
  (
    contentId: string,
    fieldIds: string[],
    onFieldsDeleted?: () => void
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    deleteFieldsInternal(contentId, fieldIds, dispatch, getState, onFieldsDeleted);
  };

/**
 * Internal helper for moving fields left or right.
 */
const moveFieldsInternal = (
  contentId: string,
  viewSettings: DatabaseViewSettings | undefined,
  moveLeft: boolean,
  fieldIds: readonly string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const database = selectDatabaseByContentIdRequired(getState(), {
    contentId
  });
  const currentView = getCachedViewSnapshot();
  const effectiveViewSettings = getViewSpecificOrderingExperimentValue()
    ? currentView.settings
    : viewSettings;
  const databaseView = effectiveViewSettings
    ? applyDatabaseView(database, database, effectiveViewSettings, getState())
    : database;
  const hiddenFields = getHiddenFields(effectiveViewSettings?.fields);

  batch(() => {
    collabDatabaseBatch({ dispatch, getState }, contentId, () => {
      fieldIds.forEach((fieldId, idx) => {
        const currentIndexInFields = databaseView.fields.indexOf(fieldId);
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.REORDER_FIELD,
            fieldId,
            hiddenFields,
            newIndex: moveLeft
              ? currentIndexInFields - 1
              : currentIndexInFields + fieldIds.length - idx + 1
          })
        );
      });
    });
  });
  reselectFieldsAfterNextLayout(fieldIds);
};

/**
 * @deprecated Use `doMoveFieldsWithIds` instead to avoid implicit selection lookup.
 */
export const doMoveFields =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined,
    moveLeft: boolean
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    moveFieldsInternal(
      contentId,
      viewSettings,
      moveLeft,
      reader.getFieldIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doMoveFieldsWithIds =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined,
    moveLeft: boolean,
    fieldIds: readonly string[]
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    moveFieldsInternal(contentId, viewSettings, moveLeft, fieldIds, dispatch, getState);
  };

/**
 * Internal helper to reduce duplication between deprecated and new action signatures,
 * maintaining parity between implicit and explicit selection inputs.
 */
const clearValuesInternal = (
  contentId: string,
  entryIds: string[],
  fieldIds: string[],
  dispatch: ThunkDispatch<DefaultRootState, void, AnyAction>,
  getState: () => DefaultRootState
) => {
  const database = selectDatabaseByContentIdRequired(getState(), {
    contentId
  });

  for (const fieldId of fieldIds) {
    const field = database.fieldsById[fieldId];
    const isPageStatus = field?.type === DatabaseFieldType.PAGE_STATUS;
    const isPageLabels = field?.type === DatabaseFieldType.PAGE_LABELS;

    if (!isPageStatus && !isPageLabels) {
      continue;
    }

    for (const entryId of entryIds) {
      const entry = getDatabaseEntryOptional(database, entryId);
      if (!entry) {
        continue;
      }

      const contentRefs = getReferencedStatusContentRefs(
        { database, field, entry },
        getState()
      );

      for (const contentRef of contentRefs) {
        if (isPageStatus) {
          applyPageStatus(contentRef, null, { getState, dispatch });
        }
        if (isPageLabels) {
          const currentLabels = getContentLabels(contentRef, {}, getState()) ?? [];
          if (currentLabels.length > 0) {
            applyPageLabels(
              contentRef,
              { add: [], remove: currentLabels },
              { getState, dispatch },
              true
            );
          }
        }
      }
    }
  }

  dispatch(
    addChangeToDraft(contentId, {
      type: DatabaseChangeType.CLEAR_ENTRIES,
      entryIds,
      fieldIds
    })
  );
};

export const doClearValues =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const reader = getService('SelectionsReader');
    clearValuesInternal(
      contentId,
      reader.getEntryIdsFromSelection(),
      reader.getFieldIdsFromSelection(),
      dispatch,
      getState
    );
  };

export const doClearValuesWithIds =
  (contentId: string, entryIds: string[], fieldIds: string[]): Dispatcher<void> =>
  (dispatch, getState) => {
    clearValuesInternal(contentId, entryIds, fieldIds, dispatch, getState);
  };
